import type { ReactNode } from 'react'
import React from 'react'
import { useNavigate } from 'react-router-dom'

import { Dashboard, PageLayout } from '@components/common'
import {
  BackButton,
  Button,
  ButtonClean,
  Loader,
  useMediaQueryContext,
  useModalContext,
} from '@fourthwall/components'
import type {
  DnsConnectionMode,
  DnsProvider,
  DnsRecord,
} from '@fourthwall/services/dist/DnsApiClient'
import { routing } from '@utils/routing'

import * as S from './DomainConfigurationView.styled'
import { DnsRecordsData } from './components/DnsRecordsData/DnsRecordsData'
import { EmailSendModal } from './components/EmailSendModal/EmailSendModal'
import type { FormValues as EmailSendFormValues } from './components/EmailSendModal/EmailSendModal.types'
import { dnsManagementUrls } from './consts'

type Props = {
  domain?: {
    fullDomain?: string
    subdomain?: string
    mainDomain?: string
  }
  mode: DnsConnectionMode | undefined
  provider?: DnsProvider
  domainDnsRecords: DnsRecord[]
  isLoading: boolean
  isPaidDomain: boolean
  isVerificationInProgress: boolean
  isAutomaticSetupInProgress: boolean
  dnsManagementUrl?: string
  warning?: ReactNode
  onSend: (values: EmailSendFormValues) => void
  onDomainVerification: () => void
  onAutomaticSetup?: () => void
}

const topContent = (
  <BackButton
    label="Domains"
    to={{
      pathname: routing.settings.domain.self,
      search: location.search,
    }}
  />
)

export const DomainConfigurationView = ({
  domain,
  provider,
  mode,
  domainDnsRecords,
  isLoading,
  isPaidDomain,
  isVerificationInProgress,
  isAutomaticSetupInProgress,
  warning,
  onSend,
  onDomainVerification,
  onAutomaticSetup,
}: Props) => {
  const media = useMediaQueryContext()
  const navigate = useNavigate()
  const { open } = useModalContext()

  if (isLoading || !domainDnsRecords.length) {
    return (
      <S.LoaderContainer>
        <Loader size="large" />
      </S.LoaderContainer>
    )
  }

  const handleSendClick = () => {
    open(EmailSendModal, { onSubmit: onSend })
  }

  const handleVerifyLater = () => {
    navigate(routing.settings.domain.self)
  }

  const isEveryRecordValueLoaded = domainDnsRecords.every((record) => record.value != null)

  const renderMainDescription = () => {
    if (mode === 'MANUAL') {
      return (
        <>
          <S.DescriptionParagraph>
            If you don’t own this domain yet, we recommend using Porkbun to purchase it. Otherwise,
            follow the instructions below to set up your domain.{' '}
            <ButtonClean
              label="Learn more"
              href="https://help.fourthwall.com/hc/en-us/articles/13331283794587-How-do-I-use-a-custom-domain"
              target="_blank"
            />
          </S.DescriptionParagraph>
          {!provider && domain?.fullDomain && (
            <S.DescriptionParagraph>
              If you just purchased {domain?.fullDomain}, we recommend waiting an hour for the new
              ownership records to update so you can use our easy, automated domain connection flow.
              If you’d like to set up your domain now, you can use the manual setup process below.
            </S.DescriptionParagraph>
          )}
        </>
      )
    }

    return (
      <S.DescriptionParagraph>
        To get your new domain working we need to enter a couple settings (called DNS records) that
        tell the domain how to work with our hosting and email system. Follow the guide below for
        instructions on how to enter these records.{' '}
        <ButtonClean
          label="Learn more"
          href="https://help.fourthwall.com/hc/en-us/articles/13331283794587-How-do-I-use-a-custom-domain"
          target="_blank"
        />
      </S.DescriptionParagraph>
    )
  }

  const renderFirstStepParagraph = () => {
    if (domain?.fullDomain && domain?.mainDomain && provider && dnsManagementUrls[provider]) {
      return (
        <S.DescriptionParagraph>
          It looks like {domain.fullDomain} is managed by {provider}.{' '}
          <ButtonClean
            appearance="primary"
            label="Click here"
            href={dnsManagementUrls[provider]?.replace('{domain}', domain.mainDomain)}
            target="_blank"
          />{' '}
          to manage your domain records.
        </S.DescriptionParagraph>
      )
    }

    return (
      <S.DescriptionParagraph>
        Login to your domain provider and find the DNS records editing page for this domain.
      </S.DescriptionParagraph>
    )
  }

  const renderDescription = () => {
    if (isPaidDomain) return null

    return (
      <S.DescriptionContent>
        {renderMainDescription()}
        <S.DescriptionPoint>1. Go to your DNS records page</S.DescriptionPoint>
        {renderFirstStepParagraph()}
        <S.DescriptionPoint>2. Enter the following values</S.DescriptionPoint>
        <S.DescriptionParagraph>
          Enter the follows DNS values for {domain?.fullDomain || 'your domain'}
        </S.DescriptionParagraph>
        {warning && <S.WarningContainer>{warning}</S.WarningContainer>}
      </S.DescriptionContent>
    )
  }

  return (
    <PageLayout
      bottomContent={
        <>
          <Dashboard.Section.Item>
            <Dashboard.Section.ScrollableContent>
              <DnsRecordsData
                records={domainDnsRecords}
                isVerificationInProgress={isVerificationInProgress}
              />
            </Dashboard.Section.ScrollableContent>
          </Dashboard.Section.Item>
          <Dashboard.Section.Item>
            <S.BottomContentContainer>
              {!isPaidDomain ? (
                <ButtonClean
                  appearance="primary"
                  label="Send this list via email"
                  onClick={handleSendClick}
                  disabled={!isEveryRecordValueLoaded}
                />
              ) : (
                <span />
              )}
              <S.RightBottomContentContainer>
                <ButtonClean
                  appearance="primary"
                  label="Verify later"
                  onClick={handleVerifyLater}
                />
                <Button
                  appearance="primary"
                  label="Verify records"
                  fullWidth={!media.tablet}
                  disabled={!isEveryRecordValueLoaded || isAutomaticSetupInProgress}
                  loading={isVerificationInProgress}
                  onClick={onDomainVerification}
                />
                {onAutomaticSetup && (
                  <Button
                    appearance="primary"
                    label="Automatic setup"
                    fullWidth={!media.tablet}
                    loading={isAutomaticSetupInProgress}
                    disabled={isVerificationInProgress}
                    onClick={onAutomaticSetup}
                  />
                )}
              </S.RightBottomContentContainer>
            </S.BottomContentContainer>
          </Dashboard.Section.Item>
        </>
      }
      bottomContentMargin="none"
    >
      <Dashboard.Section
        name={isPaidDomain ? 'DNS records' : 'Set up your new domain'}
        bottomMargin="small"
        topContent={topContent}
        bottomContent={renderDescription()}
      />
    </PageLayout>
  )
}
